import { Heart, Share2, User } from 'lucide-react';
import Button from '@/components/ui/Button';
import SocialLinks from '@/components/SocialLinks';
import LanguageSelector from '@/components/LanguageSelector';
import type { UserData } from '@/lib/firebase/user';
import { useAuth } from '@/components/AuthProvider';
import { useEffect, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import AuthModal from '@/components/auth/AuthModal';
import OnboardingModal from '@/components/ui/OnboardingModal';
import { createFreeSubscription } from '@/lib/firebase/membership/queries';
import { toast } from 'sonner';
import { buildShareUrl } from '@/lib/utm';
import { shareOrCopy } from '@/lib/shareLink';
import { trackShareClicked } from '@/lib/utils/analytics';

interface MissionHeaderProps {
  headerImage?: string;
  photoURL?: string;
  title: string;
  displayName?: string;
  postCount: number;
  social?: UserData['social'];
  creatorId: string;
  creatorPath?: string; // Friendly URL path
  allowFreeFollowing: boolean;
  // TSF: lets the header open the donate dialog even when the visitor is on
  // a tab (e.g. #about) where MissionContent -- the only 'open-donate'
  // listener -- isn't mounted. The parent switches to #home first, then
  // dispatches. Falls back to a bare dispatch if the parent doesn't wire it
  // (e.g. in isolated tests), matching every other donate trigger's pattern.
  onDonateClick?: () => void;
}

export default function MissionHeader({
  headerImage,
  photoURL,
  title,
  displayName,
  postCount,
  social,
  creatorId,
  creatorPath,
  allowFreeFollowing,
  onDonateClick
}: MissionHeaderProps) {
  console.log('[MissionHeader] Received headerImage:', headerImage);
  const { user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    async function checkSubscription() {
      if (!user?.uid || !creatorId) return;

      try {
        const q = query(
          collection(db, 'subscriptions'),
          where('userId', '==', user.uid),
          where('creatorId', '==', creatorId),
          where('status', '==', 'active')
        );

        const snapshot = await getDocs(q);
        setIsSubscribed(!snapshot.empty);
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    }

    checkSubscription();
  }, [user?.uid, creatorId]);

  const handleJoinForFree = async () => {
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      await createFreeSubscription(user.uid, creatorId);
      setIsSubscribed(true);
      setNewUserEmail(user.email || '');
      setIsOnboardingModalOpen(true);
      toast.success('Successfully joined for free!');
    } catch (error: any) {
      console.error('Error joining for free:', error);
      toast.error(error.message || 'Failed to join for free');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthSuccess = async (user: any) => {
    setIsAuthModalOpen(false);
    if (user) {
      // For new signups, show the onboarding modal immediately
      setNewUserEmail(user.email || '');
      setIsOnboardingModalOpen(true);

      // Don't try to create subscription yet - wait for email verification
      // The subscription will be created when user completes onboarding
    }
  };

  // Sharing the fund page is a much smaller ask than creating an account,
  // and it is the one that brings somebody new. buildShareUrl tags the link
  // so the arrival is attributable (see lib/utm.ts), and the medium reported
  // in the event matches the medium stamped on the link.
  const handleShare = async () => {
    const base = `${window.location.origin}/fund/${creatorPath || creatorId}`;
    trackShareClicked('fund_page', creatorId);
    // A dismissed sheet does nothing (it used to error-toast on iOS); a sheet
    // that genuinely fails falls through to the clipboard. See lib/shareLink.ts.
    const outcome = await shareOrCopy({
      shareData: { title: displayName || title, url: buildShareUrl(base, 'native_share') },
      copyText: buildShareUrl(base, 'copy_link'),
    });
    if (outcome === 'copied') {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
      toast.success('Link copied — share it to help spread the word');
    } else if (outcome === 'failed') {
      toast.error('Could not copy the link');
    }
  };

  const handleDonateClick = () => {
    if (onDonateClick) {
      onDonateClick();
    } else {
      window.dispatchEvent(new CustomEvent('open-donate', { detail: { surface: 'header' } }));
    }
  };

  const handleOnboardingComplete = () => {
    // User has completed onboarding, they can now explore the mission page
    // The modal will close and they'll see the success state
    setIsSubscribed(true);
  };

  return (
    <div className="relative">
      {/* Language selector positioned in top-right corner */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      <div className="h-44 sm:h-64 md:h-80 w-full">
        {headerImage ? (
          <img
            src={headerImage}
            alt="Fund Page Cover"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600" />
        )}
      </div>

      <div className="max-w-5xl mx-auto px-4 relative -mt-14 sm:-mt-16 md:-mt-20">
        <div className="flex flex-col items-center">
          <div className="h-28 w-28 sm:h-32 sm:w-32 md:h-40 md:w-40 rounded-full ring-4 ring-white overflow-hidden bg-white">
            {photoURL ? (
              <img
                src={photoURL}
                alt={displayName || ''}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center bg-gray-100">
                <User className="h-14 w-14 sm:h-16 sm:w-16 md:h-20 md:w-20 text-gray-400" />
              </div>
            )}
          </div>

          {!isSubscribed && allowFreeFollowing && (
            <Button
              size="lg"
              className="mt-6"
              onClick={handleJoinForFree}
              disabled={isLoading}
            >
              {isLoading ? 'Joining...' : 'Join for free'}
            </Button>
          )}

          <div className="mt-4 text-center">
            <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
          </div>

          <Button
            size="lg"
            className="mt-4 w-full max-w-xs flex items-center justify-center gap-2"
            onClick={handleDonateClick}
          >
            <Heart className="h-4 w-4" />
            Support now
          </Button>

          <SocialLinks social={social} className="mt-4" />

          <button
            type="button"
            onClick={handleShare}
            className="mt-4 inline-flex items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <Share2 className="h-4 w-4" />
            {shareCopied ? 'Link copied' : 'Share this page'}
          </button>
        </div>
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
        isDonorFlow={false}
        skipDashboardRedirect={true}
        creatorId={creatorId}
      />

      <OnboardingModal
        isOpen={isOnboardingModalOpen}
        onClose={() => setIsOnboardingModalOpen(false)}
        userEmail={newUserEmail}
        creatorName={displayName || title}
        creatorId={creatorId}
        creatorPath={creatorPath}
        onContinue={handleOnboardingComplete}
      />
    </div>
  );
}